import React, { useEffect, useMemo, useState } from 'react';
import { Template } from 'devextreme-react';
import DataGrid, { Column, SearchPanel } from 'devextreme-react/data-grid';
import { Link, useMediaQuery, useTheme } from '@material-ui/core';
import * as Icons from '@material-ui/icons';
import {
  FilterGroup,
  formatPhoneNumberIntl, IconButton, LoadingBounce, useDialogs, useListSelectFilter,
} from '@michelin/acid-components';
import { Delete as DeleteIcon, Edit } from '@material-ui/icons';
import { useTranslation, useMaestroDataSource } from '@michelin/central-provider';
import { useHistory } from 'react-router-dom';
import { getOptions } from '@michelin/select-options-provider';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import useUtils from 'hooks/useUtils';
import CreatedByAvatar from '../CreatedByAvatar';
import { contactsList } from '../../Types/APITypes';
import { ContactListItem, ContactListItemAction, SPPContactTypeRoleAreasEnum } from '../../Types';
import * as Utils from './Utils';
import { CONTENT_AREA_HEIGHT } from '../../Utils';
import useCustomer from '../../hooks/useCustomer';
import {
  GetRoleAreaLabel,
  hasContactTypePriorityLevelsAssigned,
  hasMichelinContactTypes,
} from './Utils';
import { PriorityLevelsManagementModal } from './ContactTypes/PriorityLevelsManagementModal';
import { deleteContact } from '../../hooks/useContactQueries';
import { AssignContactTypesState } from './ContactTypes/utils';

interface ContactsListTableProps {
  stNumber: string,
  btNumber: string,
  contactLevel?: 'ST' | 'BT' | 'All',
  customActions?: ContactListItemAction[],
}

export default function ContactsListTable(props: ContactsListTableProps) {
  const {
    stNumber, btNumber, contactLevel, customActions,
  } = props;

  const history = useHistory();
  const { t } = useTranslation();
  const {
    confirmDialog, alertDialog, errorDialog,
  } = useDialogs();
  const { checkLevel } = useUtils();
  const permissions = usePermissionsSPP(stNumber);
  const customerQuery = useCustomer(stNumber);
  const theme = useTheme();
  const tabletView = useMediaQuery(theme.breakpoints.down(800));
  const cellView = useMediaQuery(theme.breakpoints.down(500));

  const [saving, setSaving] = useState<boolean>(false);
  const [forceReload, setForceReload] = useState<number>(Date.now());
  const [assigningContactTypes, setAssigningContactTypes] = useState<AssignContactTypesState | null>(null);

  const [contactLevels, setContactLevels] = useState(getOptions('account_types'));
  const contactTypes = getOptions('dealer_dealer_contact_types_role_areas');

  const levelFilter = useListSelectFilter({
    label: t('Contact Level'),
    options: contactLevels,
    unselectedLabel: contactLevel === 'All' ? t('Any Account Type') : undefined,
    hashParam: !customActions ? 'accLevel' : undefined,
    defaultValue: contactLevel !== 'All' ? contactLevel : undefined,
  });

  const typeFilter = useListSelectFilter({
    label: t('Contact Type'),
    options: contactTypes,
    unselectedLabel: t('All Contact Types'),
    hashParam: !customActions ? 'contactType' : undefined,
  });

  useEffect(() => {
    if (!customerQuery.data) return;
    const filteredContactLevels = contactLevels.filter((e) => (
      (e.value === 'ST' && customerQuery.data.ship_to_customer && customerQuery.data.ship_to_customer !== 'null' && contactLevel !== 'BT')
      || (e.value === 'BT' && customerQuery.data.bill_to_customer && customerQuery.data.bill_to_customer !== 'null' && contactLevel !== 'ST')
      || (e.value === 'HO' && false)
      || (e.value === 'PC' && false)
      || (e.value === 'all')
    ));
    setContactLevels(filteredContactLevels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerQuery.data]);

  const filterGroup = useMemo(() => new FilterGroup([
    levelFilter.filter,
    typeFilter.filter,
  ]), [levelFilter.filter, typeFilter.filter]);

  const ds = useMaestroDataSource({
    API: contactsList(stNumber),
    reqOptions: {
      filters: {
        level: levelFilter.value,
        type: typeFilter.value,
      },
    },
    componentPlacements: {
      filtersPlacement: tabletView ? 'outside' : 'embeded',
      searchPlacement: cellView ? 'outside' : 'embeded',
    },
    filterGroup,
    cacheKey: forceReload.toString(),
  });

  useEffect(() => {
    async function reload() {
      await ds.dataSource.reload();
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReload]);

  /// //////////////////////////////////////////////////////////////////
  /// COLUMN RENDER METHODS
  /// //////////////////////////////////////////////////////////////////

  const renderCity = (contact: ContactListItem): JSX.Element => (
    <div>
      <b>{contact.city}</b>
      <br />
      {contact.state}
    </div>
  );

  const renderName = (contact: ContactListItem): JSX.Element => {
    const contactName: string = `${contact.first_name} ${contact.last_name}`;
    const contactId: string = contact.id;
    if (!permissions.allowsAction('contacts.read')) {
      return <b>{contactName}</b>;
    }
    if (customActions?.length) return <b>{contactName}</b>;
    return (
      <div style={{ cursor: 'pointer' }} onClick={() => history.push(`/${btNumber}/contacts/${stNumber}/view/${contactId}`)}>
        <Link>
          <b>{contactName}</b>
        </Link>
      </div>
    );
  };

  const RenderContactInformation = (contact: ContactListItem): JSX.Element => {
    const icnProps = { style: { height: 14 } };
    const contactBlock = (data: String | null, Icon: any, defaultValue: string) => {
      if (!data || !data.trim()) return null;
      return (
        <span style={{ whiteSpace: 'nowrap' }}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Icon {...icnProps} />
          {data}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {contact.preferred_method && contact.preferred_method.toLowerCase() === defaultValue.toLowerCase() ? <Icons.Star {...icnProps} /> : null}
          <br />
        </span>
      );
    };

    const wPhone = contact.work_phone || '';
    const cPhone = contact.cell_phone || '';

    let workPhone = formatPhoneNumberIntl(wPhone);
    if (workPhone && contact.ext) {
      workPhone += ` ${t('ext. {{phoneNumberExtension}}', { phoneNumberExtension: contact.ext })}`;
    }

    return (
      <div>
        {contactBlock(formatPhoneNumberIntl(cPhone), Icons.PhoneAndroid, 'cell')}
        {contactBlock(workPhone, Icons.Phone, 'work')}
        {contactBlock(contact.email_address, Icons.Email, 'email')}
      </div>
    );
  };

  const renderContactTypes = (contact: ContactListItem): JSX.Element => {
    let i = 0;
    return (
      <ul style={{ fontSize: '0.9em', whiteSpace: 'nowrap' }}>
        {Utils.GetContactTypes(contact).map((e: String) => (
          <li key={`contact-type ${i++}`}>{e}</li>
        ))}
      </ul>
    );
  };

  const renderCustomActions = (contact: ContactListItem): JSX.Element => (
    <div style={{ textAlign: 'center' }}>
      {customActions?.map((a) => {
        if (a.renderElement) return a.renderElement(a, contact);
        return (
          <div key={a.id} style={{ cursor: 'pointer' }} onClick={() => a.action(a.id, contact)}>
            <Link>
              <b>{a.label}</b>
            </Link>
          </div>
        );
      })}
    </div>
  );

  const manageContactDeletion = async (response: any) => {
    setSaving(false);
    if (response.status) {
      errorDialog(
        t('Contact could not be deleted').split('\n'),
        t('Error'),
      );
    } else {
      alertDialog(
        t('Contact has been deleted').split('\n'),
        t('Delete Contact'),
        t('Ok'),
        () => {
          setForceReload(Date.now());
        },
        {
          disableBackdropClick: true, // ff5c84d0-1a78-4758-a05a-9264f3344360
        },
      );
    }
  };

  const renderBasicActions = (contact: ContactListItem): JSX.Element => {
    const contactId: string = contact.id;
    const allowUpdate = checkLevel(permissions.location?.customer_type, contact.contact_level);

    return (
      <div>
        {permissions.allowsAction('contacts.update') && permissions.allowsAction('contacts.read') && allowUpdate ? (
          <IconButton
            aria-label="edit"
            style={{ margin: '8px' }}
            onClick={saving ? undefined : () => history.push(`/${btNumber}/contacts/${stNumber}/edit/${contactId}`)}
            size="small"
            disabled={saving}
          >
            <Edit
              fontSize="small"
              color={saving ? 'disabled' : 'primary'}
            />
          </IconButton>
        ) : null}
        {permissions.allowsAction('contacts.delete') && allowUpdate ? (
          <IconButton
            aria-label="delete"
            style={{ margin: '8px' }}
            onClick={async () => {
              setSaving(true);

              const michelinContactTypes = hasMichelinContactTypes(contact.contact_types, true);
              if (michelinContactTypes.length) {
                let contactTypeString = '';
                if (michelinContactTypes.indexOf(SPPContactTypeRoleAreasEnum.pos_administrator) !== -1) {
                  contactTypeString = GetRoleAreaLabel(SPPContactTypeRoleAreasEnum.pos_administrator);
                }

                confirmDialog(
                  // eslint-disable-next-line max-len
                  t('Deleting {{contactFirstName}} {{contactLastName}} will impact contact type priority levels assignments required for {{contactType}} profiles and preferences at this location. Reassign contact type priority levels at the location and proceed with deletion?', { contactFirstName: contact.first_name, contactLastName: contact.last_name, contactType: contactTypeString }),
                  t('Reassign priority levels and contact types'),
                  t('Yes'),
                  t('No'),
                  () => {
                    setAssigningContactTypes({
                      contact,
                      action: 'reassign',
                      force: true,
                    });
                    setSaving(false);
                  },
                  () => {
                    setSaving(false);
                  },
                  { hideCloseIcon: true, disableBackdropClick: true },
                );
              } else if (hasContactTypePriorityLevelsAssigned(contact!.contact_types)) {
                confirmDialog(
                  t('Do you wish to reassign the priority levels and contact type(s) to a different contact prior to deletion?'),
                  t('Reassign priority levels and contact types'),
                  t('Yes, Reassign'),
                  t('No, Delete'),
                  () => {
                    setAssigningContactTypes({
                      contact,
                      action: 'reassign',
                      force: false,
                    });
                    setSaving(false);
                  },
                  async () => {
                    const response = await deleteContact(stNumber, contact.id);
                    manageContactDeletion(response);
                  },
                  { hideCloseIcon: true, disableBackdropClick: true },
                );
              } else {
                const name: string = `${contact.first_name} ${contact.last_name}`;
                confirmDialog(
                  t('Are you sure you want to delete {{contactName}}?', { contactName: name }),
                  t('Delete Contact'),
                  t('Remove contact'),
                  t('Keep contact'),
                  async () => {
                    const response = await deleteContact(stNumber, contact.id);
                    manageContactDeletion(response);
                  },
                  () => {
                    setSaving(false);
                  },
                  { hideCloseIcon: true, disableBackdropClick: true },
                );
              }
            }}
            size="small"
            disabled={saving}
          >
            <DeleteIcon
              fontSize="small"
              color={saving ? 'disabled' : 'primary'}
            />
          </IconButton>
        ) : null}
      </div>
    );
  };

  const renderActions = (contact: ContactListItem): JSX.Element => {
    if (customActions?.length) return renderCustomActions(contact);
    return renderBasicActions(contact);
  };

  return (
    <>
      {assigningContactTypes
        ? (
          <PriorityLevelsManagementModal
            contactId={assigningContactTypes.contact.id}
            contactLevel={assigningContactTypes.contact.contact_level}
            firstName={assigningContactTypes.contact.first_name}
            lastName={assigningContactTypes.contact.last_name}
            stNumber={stNumber}
            btNumber={btNumber}
            countryCode={customerQuery.data.customer_country}
            action={assigningContactTypes.action}
            forceReassign={assigningContactTypes.force}
            onClose={() => {
              setAssigningContactTypes(null);
            }}
            onSave={() => {
              setForceReload(Date.now());
              setAssigningContactTypes(null);
            }}
          />
        ) : null}
      <div>
        {tabletView ? ds.renderFilterFields(null, { margin: 10 }) : undefined}
        {cellView ? <div style={{ marginBottom: -10, textAlign: 'right' }}>{ds.renderResultsCounter()}</div> : undefined}
      </div>
      {useMemo(() => (
        (customerQuery.isLoading || permissions.isLoading)
          ? <LoadingBounce style={{ height: '70vh' }} />
          : (
            <DataGrid
              style={{ height: CONTENT_AREA_HEIGHT }}
              noDataText={t('No contacts found...')}
              scrolling={{ mode: 'virtual', rowRenderingMode: 'virtual' }}
              className="freespaced-table"
              dataSource={ds.dataSource}
              allowColumnReordering
              allowColumnResizing
              wordWrapEnabled
              remoteOperations
              columnAutoWidth
              onToolbarPreparing={ds.onToolbarPreparing}
            >
              <Template name="filterFields" render={ds.renderFilterFields} />
              <Template name="resultsCounter" render={ds.renderResultsCounter} />
              <SearchPanel visible highlightSearchText={false} />
              <Column
                caption={t('Level')}
                cellRender={({ row }) => <CreatedByAvatar customerType={row.data.contact_level} />}
                alignment="center"
                dataField="contact_level"
                allowSorting
                width={100}
              />
              <Column
                caption={t('Name')}
                cellRender={({ row }) => renderName(row.data)}
                allowSorting
                dataField="full_name"
                defaultSortOrder="asc"
              />
              <Column
                caption={customerQuery.data.customer_country === 'ca' ? t('City / Province') : t('City / State')}
                cellRender={({ row }) => renderCity(row.data)}
                dataField="city_state"
                allowSorting
              />
              <Column caption={t('Email / Phone')} cellRender={({ row }) => RenderContactInformation(row.data)} />
              <Column caption={t('Contact Types')} cellRender={({ row }) => renderContactTypes(row.data)} />
              <Column
                cellRender={({ row }) => renderActions(row.data)}
                alignment="center"
                width={120}
              />
            </DataGrid>
          )
        // eslint-disable-next-line react-hooks/exhaustive-deps
      ), [permissions.isLoading, levelFilter.value, levelFilter.filter, typeFilter.value, typeFilter.filter, tabletView, cellView, ds.dataSource.totalCount(), forceReload])}
    </>
  );
}
